import { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useTenant } from "@/hooks/useTenant";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useUpdateVisit, useCancelVisit } from "@/hooks/useVisits";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { VisitForm, VisitFormData } from "./VisitForm";
import { VisitFormView } from "./VisitFormView";
import { CalendarVisit } from "@/hooks/useCalendarData";
import { Switch } from "@/components/ui/switch";
import { Mail } from "lucide-react";
import { RescheduleConfirmDialog } from "./RescheduleConfirmDialog";
import { AppointmentUpdateConfirmDialog } from "./AppointmentUpdateConfirmDialog";
import { useServices } from "@/hooks/useServices";
import { useTeamMembers } from "@/hooks/useTeamMembers";
import {
  convertLocalDateTimeToUtc,
  getTenantTimeZone,
  getTimeZoneDateKey,
  getTimeZoneTimeKey,
} from "@sylva/shared-types";

interface UpdateVisitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingVisit: CalendarVisit;
  onVisitSaved?: () => void;
}

export const UpdateVisitModal = ({
  open,
  onOpenChange,
  editingVisit,
  onVisitSaved
}: UpdateVisitModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const updateVisit = useUpdateVisit();
  const cancelVisit = useCancelVisit();
  const { allServices: services } = useServices(currentTenant?.id || null);
  const { teamMembers } = useTeamMembers(currentTenant?.id || null);
  const tenantTimeZone = getTenantTimeZone(currentTenant?.timezone);

  const [loading, setLoading] = useState(false);
  const [visitServiceIds, setVisitServiceIds] = useState<string[]>([]);
  const [visitTeamMemberIds, setVisitTeamMemberIds] = useState<string[]>([]);
  const [originalServiceIds, setOriginalServiceIds] = useState<string[]>([]);
  const [originalTeamMemberIds, setOriginalTeamMemberIds] = useState<string[]>([]);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [sendCancelNotification, setSendCancelNotification] = useState(false);
  const [isRescheduleDialogOpen, setIsRescheduleDialogOpen] = useState(false);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [pendingUpdateData, setPendingUpdateData] = useState<any>(null);
  const [newStartTime, setNewStartTime] = useState<Date | null>(null);
  const [newEndTime, setNewEndTime] = useState<Date | null>(null);
  const originalStartTime = useMemo(() => new Date(editingVisit.start_time), [editingVisit.start_time]);
  const originalEndTime = useMemo(() => new Date(editingVisit.end_time), [editingVisit.end_time]);
  const [warnings, setWarnings] = useState<Array<{type: 'capacity_approaching' | 'back_to_back_appointments'; message: string}>>([]);
  const [formData, setFormData] = useState<VisitFormData>(() => {
    const firstAppointment = editingVisit.appointments[0];
    return {
      customer_id: editingVisit.customer_id || "",
      appointment_type_id: firstAppointment?.appointment_type_id || "",
      team_member_id: firstAppointment?.team_member_id || "",
      resource_ids: firstAppointment?.resource_id ? [firstAppointment.resource_id] : [],
      date: getTimeZoneDateKey(editingVisit.start_time, tenantTimeZone),
      start_time: getTimeZoneTimeKey(editingVisit.start_time, tenantTimeZone),
      duration: 60,
      notes: editingVisit.notes || ""
    };
  });

  // Initialize visit service IDs and team member IDs from the visit's appointments
  useEffect(() => {
    if (open && editingVisit) {
      const serviceIds = editingVisit.appointments.map(apt => apt.appointment_type_id);
      const teamMemberIds = editingVisit.appointments.map(apt => apt.team_member_id || "");
      const firstAppointment = editingVisit.appointments[0];

      setVisitServiceIds(serviceIds);
      setVisitTeamMemberIds(teamMemberIds);
      setOriginalServiceIds(serviceIds);
      setOriginalTeamMemberIds(teamMemberIds);

      setFormData({
        customer_id: editingVisit.customer_id || "",
        appointment_type_id: firstAppointment?.appointment_type_id || "",
        team_member_id: firstAppointment?.team_member_id || "",
        resource_ids: firstAppointment?.resource_id ? [firstAppointment.resource_id] : [],
        date: getTimeZoneDateKey(editingVisit.start_time, tenantTimeZone),
        start_time: getTimeZoneTimeKey(editingVisit.start_time, tenantTimeZone),
        duration: 60,
        notes: editingVisit.notes || "",
      });
    }
  }, [editingVisit, open, tenantTimeZone]);

  // Compute service and team member changes
  const serviceChanges = useMemo(() => {
    if (!services || services.length === 0) {
      return [];
    }

    const changes: Array<{
      type: 'added' | 'removed' | 'unchanged';
      serviceName: string;
      serviceColor: string | null;
    }> = [];

    // Find removed services
    originalServiceIds.forEach(id => {
      if (!visitServiceIds.includes(id)) {
        const service = services.find(s => s.id === id);
        if (service) {
          changes.push({
            type: 'removed',
            serviceName: service.title,
            serviceColor: service.color,
          });
        }
      }
    });

    // Find unchanged and added services
    visitServiceIds.forEach(id => {
      const service = services.find(s => s.id === id);
      if (service) {
        if (originalServiceIds.includes(id)) {
          changes.push({
            type: 'unchanged',
            serviceName: service.title,
            serviceColor: service.color,
          });
        } else {
          changes.push({
            type: 'added',
            serviceName: service.title,
            serviceColor: service.color,
          });
        }
      }
    });

    return changes;
  }, [services, visitServiceIds, originalServiceIds]);

  // Compute team member changes
  const teamMemberChanges = useMemo(() => {
    if (!teamMembers || teamMembers.length === 0) return [];

    const changes: Array<{
      type: 'changed' | 'unchanged';
      oldTeamMemberName?: string;
      newTeamMemberName?: string;
    }> = [];

    // Only compare team members for appointments that existed originally
    // Don't include new appointments (from added services)
    const minLength = Math.min(originalTeamMemberIds.length, visitTeamMemberIds.length);

    for (let i = 0; i < minLength; i++) {
      const oldId = originalTeamMemberIds[i];
      const newId = visitTeamMemberIds[i];

      if (oldId !== newId) {
        const oldMember = teamMembers.find(tm => tm.id === oldId);
        const newMember = teamMembers.find(tm => tm.id === newId);
        changes.push({
          type: 'changed',
          oldTeamMemberName: oldMember?.name,
          newTeamMemberName: newMember?.name,
        });
      }
    }

    return changes;
  }, [teamMembers, visitTeamMemberIds, originalTeamMemberIds]);

  const handleSubmit = async () => {
    if (!currentTenant) {
      toast({ title: t("error"), description: t('noTenantSelected'), variant: "destructive" });
      return;
    }

    const startDateTime = convertLocalDateTimeToUtc(formData.date, formData.start_time, tenantTimeZone);

    // Check if time has changed
    const timeChanged = startDateTime.getTime() !== originalStartTime.getTime();

    // Check if services or team members have changed
    const servicesChanged = serviceChanges.some(sc => sc.type !== 'unchanged');
    const teamMembersChanged = teamMemberChanges.some(tm => tm.type === 'changed');

    // Calculate new end time based on the new service durations if services changed
    let calculatedEndTime: Date;
    if (servicesChanged && services && services.length > 0) {
      // Calculate total duration from the new set of services
      const totalDurationMinutes = visitServiceIds.reduce((total, serviceId) => {
        const service = services.find(s => s.id === serviceId);
        return total + (service?.duration || 0);
      }, 0);
      calculatedEndTime = new Date(startDateTime.getTime() + totalDurationMinutes * 60 * 1000);
    } else {
      // Use the original visit duration if services haven't changed
      const visitDuration = originalEndTime.getTime() - originalStartTime.getTime();
      calculatedEndTime = new Date(startDateTime.getTime() + visitDuration);
    }

    // Team member service compatibility warnings are not tracked in the warning state
    // but can be shown separately if needed in the future
    setWarnings([]);

    // Store the update data
    const updateData = {
      visit_id: editingVisit.id,
      appointment_type_ids: visitServiceIds,
      team_member_ids: visitTeamMemberIds,
      start_time: startDateTime.toISOString(),
      notes: formData.notes || null,
    };

    setPendingUpdateData(updateData);
    setNewStartTime(startDateTime);
    setNewEndTime(calculatedEndTime);

    // Decide which dialog to show based on what changed
    if (servicesChanged || teamMembersChanged) {
      // Show the comprehensive update dialog for service/team member changes
      setIsUpdateDialogOpen(true);
    } else if (timeChanged) {
      // Show the simple reschedule dialog for time-only changes
      setIsRescheduleDialogOpen(true);
    } else {
      // No changes, proceed directly without notification
      await performUpdate(updateData, false);
    }
  };

  const performUpdate = async (updateData: any, notifyCustomer: boolean) => {
    setLoading(true);

    try {
      await updateVisit.mutateAsync({
        ...updateData,
        notify_customer: notifyCustomer,
      });

      onOpenChange(false);
      setIsRescheduleDialogOpen(false);
      setIsUpdateDialogOpen(false);
      onVisitSaved?.();
    } catch (error) {
      console.error('Error updating visit:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReschedule = (sendNotification: boolean) => {
    if (pendingUpdateData) {
      performUpdate(pendingUpdateData, sendNotification);
    }
  };

  const handleConfirmUpdate = (sendNotification: boolean) => {
    if (pendingUpdateData) {
      performUpdate(pendingUpdateData, sendNotification);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleFieldChange = (field: keyof VisitFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDeleteClick = () => {
    setIsCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!editingVisit.id) {
      toast({ title: t("error"), description: "Cannot cancel: visit ID is missing", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      await cancelVisit.mutateAsync({
        visit_id: editingVisit.id,
        notify_customer: sendCancelNotification,
      });
      setIsCancelDialogOpen(false);
      onOpenChange(false);
      onVisitSaved?.();
    } catch (error) {
      console.error('Error cancelling visit:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mark / unmark the whole visit as a no-show. Only offered once the
  // appointment lies in the past. Sets both the visit and its appointments'
  // status so the calendar badge and the no-show analytics stay in sync.
  const isPastVisit = originalStartTime < new Date();

  const handleNoShow = async () => {
    if (!editingVisit.id) return;
    const newStatus = editingVisit.status === "no_show" ? "confirmed" : "no_show";
    setLoading(true);
    try {
      await updateVisit.mutateAsync({
        visit_id: editingVisit.id,
        status: newStatus,
      });
      await queryClient.invalidateQueries({ queryKey: ["calendar-appointments"] });
      onOpenChange(false);
      onVisitSaved?.();
    } catch (error) {
      console.error("Error updating no-show status:", error);
    } finally {
      setLoading(false);
    }
  };

  // Don't render anything if not open
  if (!open) return null;

  const formProps = {
    title: t("editAppointment"),
    values: formData,
    onFieldChange: handleFieldChange,
    onSubmit: handleSubmit,
    onCancel: handleCancel,
    isLoading: loading,
    showDeleteButton: true,
    onDelete: handleDeleteClick,
    showNoShowButton: isPastVisit && !!editingVisit.id,
    currentStatus: editingVisit.status,
    onNoShow: handleNoShow,
    visitServiceIds: visitServiceIds,
    visitTeamMemberIds: visitTeamMemberIds,
    onVisitChange: (serviceIds: string[], teamMemberIds: string[]) => {
      setVisitServiceIds(serviceIds);
      setVisitTeamMemberIds(teamMemberIds);
    },
    isEditingVisit: true,
  };

  return (
    <>
      {isMobile ? (
        <VisitFormView {...formProps} />
      ) : (
        <VisitForm
          open={open}
          onOpenChange={onOpenChange}
          {...formProps}
        />
      )}

      {/* Cancel Confirmation Dialog */}
      <Dialog open={isCancelDialogOpen} onOpenChange={setIsCancelDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("cancelVisitTitle")}</DialogTitle>
          </DialogHeader>

          {/* Notification Toggle */}
          <div className={`flex items-start gap-3 p-4 rounded-lg border ${
            !editingVisit.customer_email
              ? 'bg-slate-50 border-slate-200'
              : sendCancelNotification
                ? 'bg-amber-50 border-amber-200'
                : 'bg-green-50 border-green-200'
          }`}>
            <div className={`flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 mt-0.5 ${
              !editingVisit.customer_email
                ? 'bg-slate-100'
                : sendCancelNotification
                  ? 'bg-amber-100'
                  : 'bg-green-100'
            }`}>
              <Mail className={`h-3.5 w-3.5 ${
                !editingVisit.customer_email
                  ? 'text-slate-400'
                  : sendCancelNotification
                    ? 'text-amber-600'
                    : 'text-green-600'
              }`} />
            </div>
            <div className="flex-1 space-y-3">
              <div className={`text-sm ${
                !editingVisit.customer_email
                  ? 'text-slate-600'
                  : sendCancelNotification
                    ? 'text-amber-800'
                    : 'text-green-800'
              }`}>
                {!editingVisit.customer_email
                  ? t('cancelNotificationDisabled')
                  : sendCancelNotification
                    ? t('cancelNotificationEnabled')
                    : t('cancelNotificationDisabled')}
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={sendCancelNotification}
                  onCheckedChange={setSendCancelNotification}
                  disabled={!editingVisit.customer_email}
                />
                <span className={`text-sm font-medium ${
                  !editingVisit.customer_email
                    ? 'text-slate-500'
                    : sendCancelNotification
                      ? 'text-amber-800'
                      : 'text-green-800'
                }`}>
                  {!editingVisit.customer_email
                    ? t('customerHasNoEmail')
                    : sendCancelNotification
                      ? t('notificationEnabled')
                      : t('notificationDisabled')}
                </span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              onClick={() => setIsCancelDialogOpen(false)}
              disabled={loading}
            >
              {t("cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmCancel}
              disabled={loading}
            >
              {loading ? t("cancelling") : t("cancelVisit")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reschedule Confirmation Dialog - for time-only changes */}
      {newStartTime && newEndTime && (
        <RescheduleConfirmDialog
          open={isRescheduleDialogOpen}
          onOpenChange={setIsRescheduleDialogOpen}
          appointment={editingVisit.appointments[0] || null}
          customerHasEmail={!!editingVisit.customer_email}
          newStartTime={newStartTime}
          newEndTime={newEndTime}
          timeZone={tenantTimeZone}
          originalStartTime={originalStartTime}
          originalEndTime={originalEndTime}
          onConfirm={handleConfirmReschedule}
          onCancel={() => setIsRescheduleDialogOpen(false)}
        />
      )}

      {/* Appointment Update Confirmation Dialog - for service/team member changes */}
      {newStartTime && newEndTime && (
        <AppointmentUpdateConfirmDialog
          open={isUpdateDialogOpen}
          onOpenChange={setIsUpdateDialogOpen}
          customerName={editingVisit.customer_name}
          customerHasEmail={!!editingVisit.customer_email}
          oldStartTime={originalStartTime}
          oldEndTime={originalEndTime}
          newStartTime={newStartTime}
          newEndTime={newEndTime}
          timeZone={tenantTimeZone}
          serviceChanges={serviceChanges}
          teamMemberChanges={teamMemberChanges}
          warnings={warnings}
          onConfirm={handleConfirmUpdate}
          onCancel={() => setIsUpdateDialogOpen(false)}
        />
      )}
    </>
  );
};
